import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { api } from "@/lib/api";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const sampleChartData = [
  { date: "Jan 1", passed: 4200, failed: 180 },
  { date: "Jan 2", passed: 3800, failed: 220 },
  { date: "Jan 3", passed: 5100, failed: 150 },
  { date: "Jan 4", passed: 4700, failed: 90 },
  { date: "Jan 5", passed: 5300, failed: 120 },
  { date: "Jan 6", passed: 4900, failed: 85 },
  { date: "Jan 7", passed: 5800, failed: 95 },
];

const features = [
  {
    name: "DMARC Monitoring",
    description:
      "Receive and analyze DMARC aggregate reports automatically. See who's sending email on your behalf.",
    link: null,
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
  {
    name: "Source Intelligence",
    description:
      "We identify email services like Mailchimp, SendGrid, and HubSpot from IP addresses and tell you exactly how to fix issues.",
    link: null,
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
      </svg>
    ),
  },
  {
    name: "Smart Recommendations",
    description:
      "Get prioritized action items with copy-paste SPF records and links to DKIM setup guides. No more guessing.",
    link: null,
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
      </svg>
    ),
  },
  {
    name: "Weekly Digest",
    description:
      "Get a summary of your email authentication health with action items, new senders, and week-over-week trends.",
    link: null,
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
  },
  {
    name: "DNS Tools",
    description:
      "Free validators and generators for SPF, DKIM, and DMARC records. Check your setup before you deploy.",
    link: "/tools",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
      </svg>
    ),
  },
  {
    name: "Team Collaboration",
    description:
      "Invite your team with role-based access. Everyone stays aligned on email security.",
    link: null,
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
  },
  {
    name: "Website Security",
    description:
      "Scan your website for SSL issues, security headers, and blacklist status. Keep your domain's reputation protected.",
    link: "/tools/website-security-scanner",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
];

const stats = [
  { value: "Free", label: "Forever (1 Domain)" },
  { value: "< 5min", label: "Setup Time" },
  { value: "Unlimited", label: "Free DNS Tools" },
  { value: "24/7", label: "Monitoring" },
];

export function HomePage() {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const runCheck = async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await api.tools.checkAll(domain);
      setResult(data);
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const isAllPassing = result && result.spf?.valid && result.dmarc?.valid && (result.dkim?.selectors?.length > 0);
  const hasIssues = result && (!result.spf?.valid || !result.dmarc?.valid || result.dkim?.selectors?.length === 0);

  return (
    <div>
      <SEO
        title="GetSent - DMARC Monitoring & Email Authentication"
        description="Monitor your DMARC, SPF, and DKIM records with real-time reports and analytics. Protect your domain from email spoofing and improve deliverability."
        canonical="/"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-indigo-50/50 to-transparent dark:from-indigo-950/20" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[800px] w-[800px] rounded-full bg-gradient-to-r from-indigo-400/20 to-purple-400/20 blur-3xl" />
        </div>

        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-indigo-500/10 px-4 py-1.5 text-sm font-medium text-indigo-600 ring-1 ring-inset ring-indigo-500/20">
                Free forever for 1 domain
              </div>
            </div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-6xl">
              Why aren't your emails reaching your{" "}
              <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
                clients' inboxes
              </span>?
            </h1>
            <p className="mt-4 text-lg leading-8 text-muted-foreground">
              Check your domain's email setup in seconds. Free.
            </p>

            {/* Domain Test Form */}
            <div className="mt-8 mx-auto max-w-xl">
              {/* Input form - hide when results shown */}
              {!result && (
                <>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                      type="text"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      placeholder="Enter your domain (e.g. example.com)"
                      className="flex-1 rounded-full border bg-background px-6 py-4 text-base focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      onKeyDown={(e) => e.key === "Enter" && runCheck()}
                    />
                    <button
                      onClick={runCheck}
                      disabled={loading || !domain}
                      className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30 disabled:opacity-50"
                    >
                      {loading ? "Checking..." : "Check Now"}
                    </button>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="mt-4 rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-red-600 text-sm">
                      {error}
                    </div>
                  )}
                </>
              )}

              {/* Results */}
              {result && (
                <div className="mt-6 rounded-2xl border bg-card p-6 text-left shadow-lg">
                  <div className="text-sm font-medium text-muted-foreground mb-4">
                    Results for <span className="text-foreground font-semibold">{domain}</span>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    {/* SPF */}
                    <div className={`rounded-lg p-4 ${result.spf?.valid ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                      <div className="flex items-center gap-2">
                        {result.spf?.valid ? (
                          <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        <span className={`font-semibold ${result.spf?.valid ? 'text-green-600' : 'text-red-600'}`}>SPF</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {result.spf?.valid ? 'Configured' : result.spf?.error || 'Not found'}
                      </div>
                    </div>

                    {/* DKIM */}
                    <div className={`rounded-lg p-4 ${result.dkim?.selectors?.length > 0 ? 'bg-green-500/10' : 'bg-amber-500/10'}`}>
                      <div className="flex items-center gap-2">
                        {result.dkim?.selectors?.length > 0 ? (
                          <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                          </svg>
                        )}
                        <span className={`font-semibold ${result.dkim?.selectors?.length > 0 ? 'text-green-600' : 'text-amber-600'}`}>DKIM</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {result.dkim?.selectors?.length > 0 ? `${result.dkim.selectors.length} key(s) found` : 'No keys found'}
                      </div>
                    </div>

                    {/* DMARC */}
                    <div className={`rounded-lg p-4 ${result.dmarc?.valid ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                      <div className="flex items-center gap-2">
                        {result.dmarc?.valid ? (
                          <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        <span className={`font-semibold ${result.dmarc?.valid ? 'text-green-600' : 'text-red-600'}`}>DMARC</span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {result.dmarc?.valid ? `Policy: ${result.dmarc.policy || 'none'}` : result.dmarc?.error || 'Not found'}
                      </div>
                    </div>
                  </div>

                  {/* CTA based on results */}
                  <div className="mt-6 pt-4 border-t">
                    {isAllPassing ? (
                      <div className="text-center">
                        <p className="text-green-600 font-medium mb-3">
                          Great! Your email authentication is configured.
                        </p>
                        <Link
                          to="/register"
                          className="inline-flex rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition-all hover:shadow-xl hover:shadow-indigo-500/30"
                        >
                          Start Monitoring for Free
                        </Link>
                        <p className="mt-2 text-xs text-muted-foreground">
                          Get alerts when something breaks
                        </p>
                      </div>
                    ) : hasIssues ? (
                      <div className="text-center">
                        <p className="text-amber-600 font-medium mb-3">
                          We found some issues that could affect your email delivery.
                        </p>
                        <Link
                          to="/register"
                          className="inline-flex rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-amber-500/25 transition-all hover:shadow-xl hover:shadow-amber-500/30"
                        >
                          Get Help Fixing This
                        </Link>
                        <p className="mt-2 text-xs text-muted-foreground">
                          We'll show you exactly what to do
                        </p>
                      </div>
                    ) : null}
                  </div>

                  {/* Check another domain */}
                  <button
                    onClick={() => {
                      setResult(null);
                      setDomain("");
                    }}
                    className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Check another domain
                  </button>
                </div>
              )}

              {/* Helper text when no results */}
              {!result && !error && (
                <p className="mt-4 text-sm text-muted-foreground">
                  No signup required. Free instant check.
                </p>
              )}
            </div>
          </div>

          {/* Dashboard Preview - only show when no results */}
          {!result && (
            <div className="mt-12 sm:mt-16">
              <div className="relative rounded-xl bg-gradient-to-b from-muted/50 to-muted p-2 ring-1 ring-inset ring-border/50 sm:p-4">
                <div className="rounded-lg bg-background shadow-2xl ring-1 ring-border">
                  <div className="flex items-center gap-2 border-b px-4 py-3">
                    <div className="flex gap-1.5">
                      <div className="h-3 w-3 rounded-full bg-red-500" />
                      <div className="h-3 w-3 rounded-full bg-yellow-500" />
                      <div className="h-3 w-3 rounded-full bg-green-500" />
                    </div>
                    <div className="flex-1 text-center text-sm text-muted-foreground">
                      GetSent Dashboard
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid gap-4 md:grid-cols-4">
                      <div className="rounded-lg border bg-card p-4">
                        <div className="text-sm text-muted-foreground">Pass Rate</div>
                        <div className="mt-1 text-3xl font-bold text-green-600">98.5%</div>
                      </div>
                      <div className="rounded-lg border bg-card p-4">
                        <div className="text-sm text-muted-foreground">Messages</div>
                        <div className="mt-1 text-3xl font-bold">1.2M</div>
                      </div>
                      <div className="rounded-lg border bg-card p-4">
                        <div className="text-sm text-muted-foreground">Domains</div>
                        <div className="mt-1 text-3xl font-bold">24</div>
                      </div>
                      <div className="rounded-lg border bg-card p-4">
                        <div className="text-sm text-muted-foreground">Threats Blocked</div>
                        <div className="mt-1 text-3xl font-bold text-red-600">847</div>
                      </div>
                    </div>
                    <div className="mt-4 h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={sampleChartData}>
                          <defs>
                            <linearGradient id="colorPassed" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" fontSize={11} stroke="#9ca3af" />
                          <YAxis fontSize={11} stroke="#9ca3af" />
                          <Tooltip />
                          <Area
                            type="monotone"
                            dataKey="passed"
                            stroke="#22c55e"
                            fillOpacity={1}
                            fill="url(#colorPassed)"
                            name="Passed"
                          />
                          <Area
                            type="monotone"
                            dataKey="failed"
                            stroke="#ef4444"
                            fillOpacity={1}
                            fill="url(#colorFailed)"
                            name="Failed"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 text-sm font-semibold uppercase tracking-wider text-indigo-600">
              Features
            </div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-5xl">
              Everything you need for email authentication
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Comprehensive tools to monitor, analyze, and improve your email
              security posture.
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-5xl">
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => {
                const CardContent = (
                  <>
                    <div className="mb-4 inline-flex rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 p-3 text-white">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold group-hover:text-indigo-600 transition-colors">{feature.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                    {feature.link && (
                      <div className="mt-3 text-sm font-medium text-indigo-600 group-hover:text-indigo-700">
                        Try it free →
                      </div>
                    )}
                  </>
                );

                if (feature.link) {
                  return (
                    <Link
                      key={feature.name}
                      to={feature.link}
                      className="group rounded-2xl border bg-card p-6 transition-all hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/5"
                    >
                      {CardContent}
                    </Link>
                  );
                }

                return (
                  <div
                    key={feature.name}
                    className="group rounded-2xl border bg-card p-6 transition-all hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/5"
                  >
                    {CardContent}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Why GetSent */}
      <section className="border-t bg-muted/30 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 text-sm font-semibold uppercase tracking-wider text-indigo-600">
              Why GetSent
            </div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-5xl">
              We tell you what to do, not just what's wrong
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Other DMARC tools show you data and leave you to figure it out.
              GetSent identifies the problem and gives you the fix.
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-4xl">
            <div className="grid gap-8 md:grid-cols-2">
              <div className="rounded-2xl border bg-card p-8">
                <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                  Other tools
                </div>
                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-muted-foreground">"IP 192.168.1.1 failed SPF"</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-muted-foreground">Raw data dumps with no context</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 shrink-0 text-red-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-muted-foreground">You research how to fix it yourself</span>
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border-2 border-indigo-500 bg-card p-8">
                <div className="text-sm font-semibold uppercase tracking-wider text-indigo-600">
                  GetSent
                </div>
                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>"Mailchimp failed SPF — add <code className="text-xs bg-muted px-1 rounded">include:servers.mcsv.net</code>"</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Identifies services by name with one-click copy</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <svg className="h-5 w-5 shrink-0 text-green-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                    <span>Links directly to DKIM setup guides</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="border-y bg-gradient-to-r from-indigo-500 to-purple-600 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl font-bold text-white sm:text-5xl">
                  {stat.value}
                </div>
                <div className="mt-2 text-sm text-indigo-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 text-sm font-semibold uppercase tracking-wider text-indigo-600">
              How it works
            </div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-5xl">
              Get started in minutes
            </h2>
          </div>
          <div className="mx-auto mt-16 max-w-4xl">
            <div className="grid gap-8 md:grid-cols-3">
              {[
                {
                  step: "1",
                  title: "Add your domain",
                  description:
                    "Enter your domain and verify ownership with a simple DNS record.",
                },
                {
                  step: "2",
                  title: "Configure DMARC",
                  description:
                    "We'll help you set up DMARC to start receiving aggregate reports.",
                },
                {
                  step: "3",
                  title: "Monitor & improve",
                  description:
                    "Watch your dashboard as reports flow in and optimize your setup.",
                },
              ].map((item) => (
                <div key={item.step} className="text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-xl font-bold text-white">
                    {item.step}
                  </div>
                  <h3 className="mt-4 text-lg font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="relative isolate overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-16 shadow-2xl sm:px-16 sm:py-24">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Protect your domain today
              </h2>
              <p className="mt-4 text-lg text-indigo-100">
                Start using GetSent to secure your email infrastructure
                and protect your brand reputation.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link
                  to="/register"
                  className="w-full rounded-full bg-white px-8 py-4 text-base font-semibold text-indigo-600 shadow-lg transition-all hover:bg-indigo-50 sm:w-auto"
                >
                  Get started for free
                </Link>
                <Link
                  to="/pricing"
                  className="w-full rounded-full border border-white/30 px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto"
                >
                  View pricing
                </Link>
              </div>
            </div>
            {/* Decorative elements */}
            <div className="absolute -left-24 -top-24 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          </div>
        </div>
      </section>
    </div>
  );
}
